import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { User } from '../types';
import { authApi, userApi } from '../services/api';
import { secureStorage } from '../utils/secureStorage';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requires2FA: boolean;
  tempToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  verify2FA: (otp: string) => Promise<void>;
  resend2FA: () => Promise<number>;
  signup: (data: any) => Promise<void>;
  logout: (allDevices?: boolean) => Promise<void>;
  refreshUser: () => Promise<void>;
  verifyEmail: (email: string, otp: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);


// Where to land after signing in. Defaults to the dashboard, but honours a
// `?redirect=` on the login URL so a flow that had to interrupt for sign-in can
// resume exactly where it was — the OAuth consent screen depends on this,
// because losing its query string loses the PKCE challenge and the client has
// to start over with no explanation.
//
// Only same-origin relative paths are accepted. A bare `/` prefix is not enough
// on its own: `//evil.example` is protocol-relative and would leave the site.
function safeRedirectTarget(): string | null {
  const target = new URLSearchParams(window.location.search).get('redirect');
  if (!target) return null;
  if (!target.startsWith('/') || target.startsWith('//')) return null;
  return target;
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = await secureStorage.get('accessToken');
    if (token) {
      try {
        const response = await userApi.getMe();
        setUser(response.data.user);
      } catch (error) {
        await secureStorage.remove('accessToken');
        await secureStorage.remove('refreshToken');
      }
    }
    setIsLoading(false);
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await authApi.login({ email, password });
      const data = response.data;

      if (data.requires2FA) {
        setRequires2FA(true);
        setTempToken(data.tempToken);
        toast.success('Please enter your 2FA code');
      } else {
        const { user, tokens } = data;
        await secureStorage.set('accessToken', tokens.accessToken);
        await secureStorage.set('refreshToken', tokens.refreshToken);
        setUser(user);
        toast.success('Welcome back!');
        const resume = safeRedirectTarget();
        if (resume) {
          navigate(resume, { replace: true });
        } else {
          const isNewUser = !user.naicsCodes || user.naicsCodes.length === 0;
          navigate(isNewUser ? '/opportunities' : '/dashboard');
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Login failed');
      throw error;
    }
  };

  const verify2FA = async (otp: string) => {
    if (!tempToken) return;

    try {
      const response = await authApi.verify2FA({ tempToken, otp });
      const { user, tokens } = response.data;

      await secureStorage.set('accessToken', tokens.accessToken);
      await secureStorage.set('refreshToken', tokens.refreshToken);
      setUser(user);
      setRequires2FA(false);
      setTempToken(null);

      toast.success('Welcome back!');
      // Same resume rule as the password path — a user with 2FA on must not
      // lose the flow they were part-way through.
      const resume = safeRedirectTarget();
      if (resume) {
        navigate(resume, { replace: true });
      } else {
        const isNewUser = !user.naicsCodes || user.naicsCodes.length === 0;
        navigate(isNewUser ? '/opportunities' : '/dashboard');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Invalid code');
      throw error;
    }
  };

  /**
   * Ask for a fresh 2FA code. Resolves with the number of seconds the caller
   * should wait before offering another resend, so the button's countdown is
   * driven by the server rather than a guess on the client.
   */
  const resend2FA = async (): Promise<number> => {
    if (!tempToken) return 0;

    try {
      const response = await authApi.resend2FA({ tempToken });
      toast.success(response.data.message);
      return response.data.retryAfter ?? 60;
    } catch (error: any) {
      const data = error.response?.data;
      toast.error(data?.error || 'Failed to resend code');

      // The 2FA session died. Drop back to the password form rather than
      // leaving the user typing codes into a session that can never verify.
      if (error.response?.status === 401) {
        setRequires2FA(false);
        setTempToken(null);
        return 0;
      }

      // A cooldown rejection still carries a countdown — hand it back so the
      // button stays disabled in step with the server instead of inviting
      // another rejection.
      if (typeof data?.retryAfter === 'number') {
        return data.retryAfter;
      }

      throw error;
    }
  };

  const signup = async (data: any) => {
    try {
      const response = await authApi.signup(data);
      toast.success(response.data.message);
      navigate('/verify-email', { state: { email: data.email } });
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Signup failed');
      throw error;
    }
  };

  const logout = async (allDevices = false) => {
    try {
      await authApi.logout({ allDevices });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      await secureStorage.remove('accessToken');
      await secureStorage.remove('refreshToken');
      setUser(null);
      setRequires2FA(false);
      setTempToken(null);
      navigate('/');
      toast.success('Logged out successfully');
    }
  };

  const refreshUser = async () => {
    try {
      const response = await userApi.getMe();
      setUser(response.data.user);
    } catch (error) {
      console.error('Refresh user error:', error);
    }
  };

  const verifyEmail = async (email: string, otp: string) => {
    try {
      const response = await authApi.verifyEmail({ email, otp });
      toast.success(response.data.message);
      navigate('/login');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Verification failed');
      throw error;
    }
  };

  const resendVerification = async (email: string) => {
    try {
      await authApi.resendVerification({ email });
      toast.success('Verification email sent');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to resend');
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        requires2FA,
        tempToken,
        login,
        verify2FA,
        resend2FA,
        signup,
        logout,
        refreshUser,
        verifyEmail,
        resendVerification,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
